import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAccount } from '../components/AccountLayout';
import { ExternalLinkIcon } from '../components/ExternalLinkIcon';
import { PlanStats } from '../components/PlanStats';
import { CodeBlock, CopyButton, KeyReveal, StatTile } from '../components/RpcUi';
import { Select } from '../components/Select';
import { DOCS_URL, KEY_PLACEHOLDER, RPC_CHAINS, rpcUrl, VERIFY_URL } from '../lib/chains';
import { takePlanIntent } from '../lib/intent';
import { compact, count, formatDate, usd } from '../lib/money';
import { activeKeys, meteredBy, rpcApi, type RpcSummary } from '../lib/rpc-client';
import { SaasError } from '../lib/saas-client';
import {
  snippet,
  SNIPPET_LANGS,
  type KeyPlacement,
  type SnippetLang,
} from '../lib/snippets';

/** Stripe's webhook can land a few seconds after the redirect back here, so
 *  after a purchase the summary is re-read a few times. */
const AFTER_CHECKOUT_POLLS = 6;
const AFTER_CHECKOUT_POLL_MS = 5000;

/** A postpaid plan: what this period has run up, invoiced when it ends. */
function PostpaidStats({ s }: { s: RpcSummary }) {
  const per = meteredBy(s.plan);
  return (
    <section className="rpc-stats" aria-label="Account summary">
      <StatTile
        label="Plan"
        testid="stat-plan"
        value={s.plan.display_name}
        sub={
          <>
            Invoiced monthly · <Link to="/billing">Billing</Link>
          </>
        }
      />
      <StatTile
        label={`${per === 'requests' ? 'Requests' : 'Units'} this period`}
        testid="stat-units"
        value={compact(s.units.used)}
        sub={<>Invoiced {formatDate(s.period.end)}</>}
      />
      <StatTile
        label="Usage this period"
        testid="stat-balance"
        value={usd(s.caps.period_spend_micros)}
        sub={s.billing.card_on_file ? 'Card on file' : 'No card on file'}
      />
      <StatTile
        label="Rate limit"
        testid="stat-rps"
        value={`${count(s.plan.requests_per_second)} req/s`}
        sub={<>Per key, on your plan</>}
      />
    </section>
  );
}

function QuickStart() {
  const { keys, lastMinted, setLastMinted, reloadKeys } = useAccount();
  const [chainId, setChainId] = useState(1);
  const [lang, setLang] = useState<SnippetLang>('curl');
  const [placement, setPlacement] = useState<KeyPlacement>('path');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const key = lastMinted?.key ?? KEY_PLACEHOLDER;
  const haveKey = !!lastMinted;
  const url = placement === 'path' ? rpcUrl(chainId, key) : rpcUrl(chainId);

  async function onCreateFirst() {
    setBusy(true);
    setError(null);
    try {
      setLastMinted(await rpcApi.createKey({ name: 'default' }));
      await reloadKeys();
    } catch (e) {
      setError(e instanceof SaasError ? e.message : 'Could not create a key.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel" data-testid="quick-start">
      <div className="panel-eyebrow">Quick start</div>
      <h2>Make your first call</h2>
      {keys && activeKeys(keys).length === 0 && !lastMinted ? (
        <div className="rpc-first-key">
          <p className="panel-description">
            You need an API key first.
          </p>
          <button
            className="btn btn-primary"
            type="button"
            onClick={onCreateFirst}
            disabled={busy}
            data-testid="create-first-key"
          >
            {busy ? 'Creating…' : 'Create your first key'}
          </button>
          {error && <p className="form-error">{error}</p>}
        </div>
      ) : (
        <p className="panel-description">
          {haveKey ? (
            <>
              These snippets use the key you just created (<strong>{lastMinted.name}</strong>).
              Once you leave this page we can't show it again.
            </>
          ) : (
            <>
              Replace <code>{KEY_PLACEHOLDER}</code> with the key you saved when you created it.
              Lost it? <Link to="/keys">Rotate the key</Link> to get a new one.
            </>
          )}
        </p>
      )}

      {lastMinted && (
        <KeyReveal minted={lastMinted} onDismiss={() => setLastMinted(null)} showUrl={false} />
      )}

      <div className="rpc-qs-controls">
        <label className="rpc-field">
          <span>Chain</span>
          <Select
            value={String(chainId)}
            onChange={(v) => setChainId(Number(v))}
            options={RPC_CHAINS.map((c) => ({ value: String(c.id), label: `${c.name} (${c.id})` }))}
            data-testid="qs-chain"
          />
        </label>
        <div className="rpc-field">
          <span>Key goes in</span>
          <div className="rpc-segmented" role="group" aria-label="Key placement">
            {(['path', 'header'] as const).map((p) => (
              <button
                key={p}
                type="button"
                className={placement === p ? 'is-active' : undefined}
                aria-pressed={placement === p}
                onClick={() => setPlacement(p)}
                data-testid={`qs-placement-${p}`}
              >
                {p === 'path' ? 'The URL' : 'A header'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="rpc-url-row">
        <span className="rpc-url-label">Your RPC URL</span>
        <code className="rpc-url" data-testid="qs-url">
          {url}
        </code>
        <CopyButton text={url} />
      </div>
      {placement === 'header' && (
        <p className="muted rpc-small">
          Send the key as <code>Authorization: Bearer {'<key>'}</code> or{' '}
          <code>X-API-Key: {'<key>'}</code>.
        </p>
      )}

      <div className="rpc-tabs" role="tablist" aria-label="Language">
        {SNIPPET_LANGS.map((l) => (
          <button
            key={l.id}
            type="button"
            role="tab"
            aria-selected={lang === l.id}
            className={lang === l.id ? 'rpc-tab is-active' : 'rpc-tab'}
            onClick={() => setLang(l.id)}
            data-testid={`qs-lang-${l.id}`}
          >
            {l.label}
          </button>
        ))}
      </div>
      <CodeBlock code={snippet(lang, chainId, key, placement)} testid="qs-snippet" />

      <p className="muted rpc-small">
        Next:{' '}
        <a href={`${DOCS_URL}/reference/methods/`} target="_blank" rel="noreferrer">
          the <ExternalLinkIcon>methods</ExternalLinkIcon>
        </a>
        ,{' '}
        <a href={`${DOCS_URL}/proofs/`} target="_blank" rel="noreferrer">
          <ExternalLinkIcon>proofs</ExternalLinkIcon>
        </a>
        , and{' '}
        <a href={VERIFY_URL} target="_blank" rel="noreferrer">
          verifying a log proof in your <ExternalLinkIcon>browser</ExternalLinkIcon>
        </a>
        .
      </p>
    </section>
  );
}

export function AccountPanel() {
  const { summary, reloadSummary } = useAccount();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const purchase = params.get('rpc');
  const [notice, setNotice] = useState<string | null>(
    purchase === 'credits_added'
      ? 'Payment received. Your credit appears here as soon as Stripe confirms it.'
      : purchase === 'subscribed'
        ? 'Subscription started. Your plan updates here as soon as Stripe confirms the payment.'
        : purchase === 'card_saved'
          ? 'Card saved. It shows here as soon as Stripe confirms it.'
          : null,
  );

  // A plan picked on the pricing page before signing up: go finish it.
  useEffect(() => {
    const plan = takePlanIntent();
    if (plan) navigate(`/billing?plan=${plan}`, { replace: true });
  }, [navigate]);

  // Back from Stripe Checkout: re-read until the webhook has landed.
  useEffect(() => {
    if (!purchase) return;
    let n = 0;
    const id = window.setInterval(() => {
      n += 1;
      reloadSummary();
      if (n >= AFTER_CHECKOUT_POLLS) window.clearInterval(id);
    }, AFTER_CHECKOUT_POLL_MS);
    return () => window.clearInterval(id);
  }, [purchase, reloadSummary]);

  function dismissNotice() {
    setNotice(null);
    setParams(
      (curr) => {
        const next = new URLSearchParams(curr);
        next.delete('rpc');
        next.delete('oauth');
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  }

  const s = summary;
  return (
    <>
      {notice && (
        <div className="rpc-notice rpc-notice--ok rpc-notice--row" role="status" data-testid="purchase-notice">
          <span>{notice}</span>
          <button className="btn-link" type="button" onClick={dismissNotice}>
            Dismiss
          </button>
        </div>
      )}

      {s?.plan.postpaid ? <PostpaidStats s={s} /> : <PlanStats s={s} />}

      <QuickStart />
    </>
  );
}
